import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, UserX, RotateCcw, RefreshCw, Flag, Languages } from 'lucide-react';
import { getMediaUrl } from '../utils/mediaUrl';

// Admin › Modération › groupes système (GPT Guyane / GPT Africa) : membres + exclusions.
const SystemGroupsModeration = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/system-groups');
      setGroups(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exclude = async (g, u) => {
    const reason = window.prompt(`Exclure ${u.name || u.username || u.id} de « ${g.name} » ? Motif (optionnel) :`, '');
    if (reason === null) return;
    try {
      await axios.post(`/api/admin/system-groups/${g.id}/exclude/${u.id}`, { reason });
      toast.success('Utilisateur exclu — ses traductions sont conservées');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const unexclude = async (g, u) => {
    try {
      await axios.delete(`/api/admin/system-groups/${g.id}/exclude/${u.id}`);
      toast.success('Utilisateur réintégré');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const Avatar = ({ u }) => {
    const src = u.profile_image_url || u.avatar_url || u.picture;
    return src ? (
      <img src={getMediaUrl(src)} alt="" className="w-9 h-9 rounded-full object-cover" />
    ) : (
      <div className="w-9 h-9 rounded-full bg-violet-700/40 flex items-center justify-center text-sm font-bold">{(u.name || u.username || '?')[0]?.toUpperCase()}</div>
    );
  };

  return (
    <div className="space-y-4" data-testid="system-groups-moderation">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg flex items-center gap-2"><Languages className="w-5 h-5 text-emerald-400" /> Groupes GPT Guyane & GPT Africa</h3>
          <p className="text-sm text-gray-400">Membres de chaque groupe système. Un utilisateur exclu ne voit plus le groupe ; ses traductions restent dans le corpus.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-white/10 text-gray-300" data-testid="system-groups-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>
      {!loading && groups.length === 0 && <p className="text-gray-500 italic text-sm">Aucun groupe système créé pour le moment.</p>}
      {groups.map((g) => {
        const isOpen = open[g.id] ?? (g.system_kind === 'regional_lang_guyane');
        return (
          <div key={g.id} className="rounded-xl bg-white/5 border border-white/10" data-testid={`system-group-${g.id}`}>
            <button onClick={() => setOpen((o) => ({ ...o, [g.id]: !isOpen }))} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`system-group-toggle-${g.id}`}>
              <Users className="w-5 h-5 text-violet-300" />
              <div className="flex-1">
                <p className="font-semibold">{g.name}</p>
                <p className="text-xs text-gray-400">{g.model}{g.country ? ` · ${g.country}` : ''} · {g.members_count} membre{g.members_count > 1 ? 's' : ''}{g.banned?.length ? ` · ${g.banned.length} exclu(s)` : ''}</p>
              </div>
              <span className="text-xs text-gray-500">{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-2">
                {g.members.length === 0 && <p className="text-xs text-gray-500 italic">Aucun membre</p>}
                {g.members.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 p-2 rounded-lg bg-black/20" data-testid={`system-group-member-${g.id}-${u.id}`}>
                    <Avatar u={u} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.name || u.username || u.id}</p>
                      <p className="text-xs text-gray-400 truncate">@{u.username || '?'} · {u.contributions || 0} traduction{(u.contributions || 0) > 1 ? 's' : ''}{u.pending_reports ? <span className="text-amber-300 ml-1 inline-flex items-center gap-0.5"><Flag className="w-3 h-3" /> {u.pending_reports}</span> : null}</p>
                    </div>
                    {u.protected ? (
                      <span className="px-2 py-1 text-[10px] rounded bg-white/10 text-gray-400" data-testid={`protected-${g.id}-${u.id}`}>Admin</span>
                    ) : (
                    <button onClick={() => exclude(g, u)} className="px-3 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/30 text-red-300 text-xs font-semibold flex items-center gap-1" data-testid={`exclude-${g.id}-${u.id}`}>
                      <UserX className="w-3.5 h-3.5" /> Exclure
                    </button>
                    )}
                  </div>
                ))}
                {g.banned?.length > 0 && (
                  <div className="pt-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Exclus</p>
                    {g.banned.map((u) => (
                      <div key={u.id} className="flex items-center gap-3 p-2 rounded-lg bg-red-500/5 border border-red-500/10" data-testid={`system-group-banned-${g.id}-${u.id}`}>
                        <Avatar u={u} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{u.name || u.username || u.id}</p>
                          <p className="text-xs text-gray-400 truncate">{u.reason || 'Sans motif'} · {u.banned_at?.slice(0, 10)}</p>
                        </div>
                        <button onClick={() => unexclude(g, u)} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1" data-testid={`unexclude-${g.id}-${u.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" /> Réintégrer
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SystemGroupsModeration;
